import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Accordion, AccordionItem, AccordionContent } from '@/components/ui/accordion';
import { ChevronDown } from 'lucide-react';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { cn } from '@/lib/utils';

interface AccordionSectionProps {
  label: string | React.ReactNode;
  content: React.ReactNode;
  handleValueChange?: (value: string) => void;
  defaultOpen?: boolean;
  variant?: 'filter' | 'card';
  customChevron?: React.ReactNode;
  chevronPosition?: 'left' | 'right';
  showViewDetailsText?: boolean;
}

export const AccordionSection = ({
  label,
  content,
  handleValueChange,
  defaultOpen = false,
  variant = 'filter',
  customChevron,
  chevronPosition = 'right',
  showViewDetailsText = false,
}: AccordionSectionProps) => {
  const chevronElement = customChevron ?? (
    <ChevronDown className="h-6 w-6 shrink-0 transition-transform duration-200" />
  );

  const detailsTextElement = showViewDetailsText && (
    <span className="text-subtext text-sm self-start">
      <span className="group-data-[state=closed]:inline group-data-[state=open]:hidden">View details</span>
      <span className="group-data-[state=open]:inline group-data-[state=closed]:hidden">Hide details</span>
    </span>
  );

  return (
    <Accordion
      type="single"
      collapsible
      variant={variant}
      defaultValue={defaultOpen ? 'item-1' : undefined}
      onValueChange={handleValueChange}
    >
      <AccordionItem value="item-1" variant={variant}>
        <AccordionPrimitive.Trigger
          className={cn(
            // Base styles
            'group font-semibold w-full flex flex-1 items-center justify-between py-4 px-2 transition-all dm-text-primary',
            // Chevron rotation based on position: left chevrons rotate +90 (down), right chevrons rotate -90 (up)
            chevronPosition === 'left'
              ? '[&[data-state=open]>svg]:rotate-90 [&[data-state=open]>div>svg]:rotate-90'
              : '[&[data-state=open]>svg]:-rotate-90',
            // Card variant styles
            variant === 'card' && [
              'p-0 py-4 text-sm border border-transparent rounded-lg',
              ' hover:bg-[#F6F7F8]',
              chevronPosition !== 'left' && '[&[data-state=open]>div>svg]:-rotate-90',
              'data-[state=open]:border-b-neutral-100 data-[state=open]:rounded-b-none',
            ]
          )}
          data-testid={generateTestId(`filter_${label}`)}
        >
          {chevronPosition === 'left' && chevronElement}
          <div className="flex flex-col gap-2 flex-1 ">
            {label}
            {detailsTextElement}
          </div>
          {chevronPosition === 'right' && chevronElement}
        </AccordionPrimitive.Trigger>

        <AccordionContent variant={variant}>{content}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
